import { injectable } from "inversify";
import { makeAutoObservable } from "mobx";
import type { CoachAnimationState, ICoachManager } from "@/lib/types/coach";

/**
 * Coach Store - Live coach animation state for public training
 *
 * Mirrors the dashboard CoachManager: the avatar live session emits
 * `coach_mode_activated` / `coach_mode_deactivated` events, the store turns
 * them into animation states and the Coach component plays the matching
 * Lottie segment.
 */
@injectable()
export class LandingCoachStore implements ICoachManager {
  animationState: CoachAnimationState = "initial";
  isInitialPlayed: boolean = false;
  isEnabled: boolean = true;
  private pendingState: CoachAnimationState | null = null;
  private isCoachSpeaking = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setEnabled(value: boolean): void {
    this.isEnabled = value;
  }

  private transition(state: CoachAnimationState): void {
    if (!this.isInitialPlayed) {
      this.pendingState = state;
      return;
    }
    this.animationState = state;
  }

  completeInitial(): void {
    if (this.animationState === "initial" && !this.isInitialPlayed) {
      this.isInitialPlayed = true;
      this.animationState = this.pendingState ?? "idle";
      this.pendingState = null;
    }
  }

  startSpeaking(): void {
    this.isCoachSpeaking = true;
    if (this.animationState === "speaking") return;
    this.transition("speaking");
  }

  stopSpeaking(): void {
    this.isCoachSpeaking = false;
    if (this.pendingState === "speaking" || this.animationState === "speaking") {
      this.transition("idle");
    }
  }

  startListening(): void {
    if (this.animationState === "listening") return;
    this.transition("listening");
  }

  stopListening(): void {
    if (this.pendingState === "listening" || this.animationState === "listening") {
      this.transition(this.isCoachSpeaking ? "speaking" : "idle");
    }
  }

  startThinking(): void {
    if (!this.isInitialPlayed || this.animationState !== "idle") return;
    this.animationState = "thinking";
  }

  stopThinking(): void {
    if (this.animationState === "thinking") {
      this.animationState = "idle";
    }
  }

  reset(): void {
    this.isInitialPlayed = false;
    this.animationState = "initial";
    this.pendingState = null;
    this.isCoachSpeaking = false;
  }
}
